import React, { useState, useRef, useMemo, useEffect } from "react"
import { Popover, PopoverBody } from "reactstrap"
import gql from "graphql-tag"
import { useQuery, useMutation } from "@apollo/client"
import "./ReactionsPopup.css"
import { ReactComponent as AddReaction } from '../../assets/images/icons/add-reaction.svg'

const GET_REACTION_CHOICES = gql`
  query GetCommentReactions {
    commentReactions: __type(name: "TaskRabbitTaskCommentReactionReactionChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const GET_COMMENT_REACTIONS = gql`
  query AllTaskCommentReactions($commentId: ID!) {
    allTaskCommentReactions(commentId: $commentId) {
      edges {
        node {
          id
          reaction
          createdBy {
            id
            username
            firstName
            lastName
          }
          comment {
            id
          }
        }
      }
    }
  }
`

const GET_CURRENT_USER = gql`
  query CurrentUser {
    currentUser {
      id
      username
    }
  }
`

const CREATE_REACTION = gql`
  mutation CreateTaskCommentReaction($input: CreateTaskCommentReactionMutationInput!) {
    createTaskCommentReaction(input: $input) {
      ok
      errors { messages }
    }
  }
`

const DELETE_REACTION = gql`
  mutation deleteTaskCommentReaction($input: DeleteTaskCommentReactionMutationInput!) {
    deleteTaskCommentReaction(input: $input) {
      ok
      errors { messages }
    }
  }
`

const EMOJIS = {
    LIKE: "👍",
    LOVE: "❤️",
    HAHA: "😂",
    WOW: "😮",
    SAD: "😢",
    ANGRY: "😡"
}

export default function ReactionsPopup({ commentId }) {
    const pickerTargetRef = useRef(null)
    const { data: choicesData } = useQuery(GET_REACTION_CHOICES)
    const reactionChoices = choicesData?.commentReactions?.enumValues || []

    const { data, loading: reactionLoading, refetch } = useQuery(GET_COMMENT_REACTIONS, {
        variables: { commentId },
        fetchPolicy: "network-only"
    })
    const reactionNodes = useMemo(() => data?.allTaskCommentReactions?.edges?.map(e => e.node) || [], [data])

    const { data: currentUserData } = useQuery(GET_CURRENT_USER)
    const currentUser = currentUserData?.currentUser || null

    const [createReaction] = useMutation(CREATE_REACTION)
    const [deleteReaction] = useMutation(DELETE_REACTION)

    const [hoveredReaction, setHoveredReaction] = useState(null)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const myReaction = useMemo(() => {
        if (!currentUser) return null
        return reactionNodes.find(r => r.createdBy.id === currentUser.id || r.createdBy.username === currentUser.username)
    }, [reactionNodes, currentUser])

    const groupedReactions = useMemo(() => {
        const grouped = {}
        reactionNodes.forEach(r => {
            const key = r.reaction
            if (!grouped[key]) grouped[key] = { count: 0, users: [] }
            grouped[key].count++
            grouped[key].users.push(r)
        })
        return grouped
    }, [reactionNodes])

    const hasReactions = Object.keys(groupedReactions).length > 0

    const handleReaction = async reactionKey => {
        if (!commentId || loading) return
        setLoading(true)
        try {
            if (myReaction) {
                if (myReaction.reaction === reactionKey) {
                    await deleteReaction({
                        variables: { input: { commentId: commentId } }
                    })
                } else {
                    await createReaction({
                        variables: { input: { commentId, reaction: reactionKey } }
                    })
                }
            } else {
                await createReaction({
                    variables: { input: { commentId, reaction: reactionKey } }
                })
            }

            await refetch()
            setPickerOpen(false)
        } catch (err) {
            console.error("Reaction mutation error:", err)
        } finally {
            setLoading(false)
        }
    }

    const handleOutsideClick = event => {
        if (pickerTargetRef.current && !pickerTargetRef.current.contains(event.target)) {
            setPickerOpen(false) // Close popover if clicked outside
        }
    }

    useEffect(() => {
        if (pickerOpen) {
            document.addEventListener("click", handleOutsideClick)
        } else {
            document.removeEventListener("click", handleOutsideClick)
        }
        return () => document.removeEventListener("click", handleOutsideClick)
    }, [pickerOpen])

    return (
        <div className="reactions-popup">
            {reactionLoading ? <i className="fa fa-spinner fa-spin" /> : hasReactions && Object.entries(groupedReactions).map(([reactionKey, value], index) => {
                const emoji = EMOJIS[reactionKey] || "👍"
                const pillId = `reaction-pill-${index}-${reactionKey}`
                return (
                    <React.Fragment key={reactionKey}>
                        <button type="button" id={pillId} className="reaction-pill"
                            onMouseEnter={() => setHoveredReaction(reactionKey)}
                            onMouseLeave={() => setHoveredReaction(null)}
                            onClick={() => handleReaction(reactionKey)}
                        >
                            <div className="d-flexd-flex justify-content-center align-items-center">
                                <span className="reaction-pill-emoji">{emoji}</span>
                                <span className="reaction-pill-count">{value.count}</span>
                            </div>
                        </button>
                        <Popover placement="top" isOpen={hoveredReaction === reactionKey} target={pillId} className="reaction-hover-popper">
                            <PopoverBody>
                                <div className="reaction-popover-inner">
                                    <p className="reaction-popover-emoji">{emoji}</p>
                                    <p className="reaction-users mb-0">
                                        {(() => {
                                            const userNames = value.users.map(({ createdBy }) => `${createdBy.firstName} ${createdBy.lastName}`)
                                            const formattedNames = userNames.length > 1 ? `${userNames.slice(0, -1).join(", ")} and ${userNames[userNames.length - 1]}` : userNames[0]
                                            return (
                                                <small>{formattedNames} reacted with :{reactionKey}:</small>
                                            )
                                        })()}
                                    </p>
                                </div>
                            </PopoverBody>
                        </Popover>
                    </React.Fragment>
                )
            })
            }
            {!reactionLoading && <>
                <button type="button" id="add-reaction-button" ref={pickerTargetRef} className="reaction-add-button" onMouseEnter={() => setPickerOpen(true)}>
                    {hasReactions ? <AddReaction width={15} height={15} /> : <i className={"fa fa-thumbs-up"} />}
                </button>

                <Popover placement="right" isOpen={pickerOpen} target={pickerTargetRef.current} className="reaction-picker-popper">
                    <PopoverBody className="reaction-picker-body">
                        {reactionChoices.filter(reaction => EMOJIS[reaction.name]).map(reaction => (
                            <button key={reaction.name} type="button" className="reaction-picker-emoji" onClick={() => handleReaction(reaction.name)}>
                                {EMOJIS[reaction.name]}
                            </button>
                        ))}
                    </PopoverBody>
                </Popover>
            </>}
        </div>
    )
}
